/**
 * patientForms.ts — the PATIENT self-serve "My forms" reads (authed PATIENT).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 10 (patient
 * portal basis, 2026-07-22).
 *
 * WHY: the patient portal's "My forms" section lets a signed-in patient see the
 * questionnaires THEY submitted, read-only, with the clinician's
 * patient-visible notes shown against them. These are PATIENT self-scoped
 * clinic-service reads (R41): the gateway binds the caller to
 * Assessment.patientUserId (set at checkout, Wave 6) and only ever serves the
 * caller's OWN rows — the portal only ever asks for `/mine` (anti-enumeration:
 * a patient can never read another patient's assessment by id).
 *
 * [CONTRACTS GAP] The three `/mine` reads are mirrored locally (strict shapes in
 * api/types.ts, fixtures in testing/fixtures.ts) until the clinic-service batch
 * lands — the established pattern for a portal read ahead of its backend.
 *
 * PREFIXING: like every other client here, paths are `/v1/*` — the shared
 * request client prepends `GATEWAY_URL` (same-origin `/api` by default), so the
 * wire path is `/api/v1/clinic/assessments/mine…`.
 */
import { get } from './client';
import type { PatientAssessmentDetail, PatientAssessmentSummary, PatientVisibleNote } from './types';

/**
 * GET /v1/clinic/assessments/mine → the caller's own submitted forms (newest
 * first, server-ordered). The backend wraps the rows in `{ items }` — unwrap
 * to the summary array the "My forms" list renders.
 */
export function getMyAssessments(): Promise<PatientAssessmentSummary[]> {
  return get<{ items: PatientAssessmentSummary[] }>('/v1/clinic/assessments/mine', true).then(
    (r) => r.items,
  );
}

/**
 * GET /v1/clinic/assessments/mine/detail?assessmentId= → one of the caller's own
 * forms + its answer projection. A LITERAL `mine/detail` path with a query id
 * (mirroring the clinic-service backend, which uses literals so a `mine/:id`
 * param can't shadow the sibling `mine/notes` route — AssessmentsModule
 * registers before AssessmentNotesModule).
 */
export function getMyAssessment(assessmentId: string): Promise<PatientAssessmentDetail> {
  return get(`/v1/clinic/assessments/mine/detail?assessmentId=${encodeURIComponent(assessmentId)}`, true);
}

/**
 * GET /v1/clinic/assessments/mine/notes?assessmentId= → the clinician notes the
 * patient may see against this form (patientVisible=true only). The backend
 * wraps the notes in `{ items }` — unwrap to the note array the detail renders.
 */
export function getMyAssessmentNotes(assessmentId: string): Promise<PatientVisibleNote[]> {
  return get<{ items: PatientVisibleNote[] }>(
    `/v1/clinic/assessments/mine/notes?assessmentId=${encodeURIComponent(assessmentId)}`,
    true,
  ).then((r) => r.items);
}
